import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { DateFormatProvider } from '@/context/DateFormatContext';
import { Toaster } from '@/components/ui/toaster';

// Pages
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ForcePasswordChangePage from '@/pages/ForcePasswordChangePage';
import AuthCallback from '@/pages/AuthCallback';
import DashboardPage from '@/pages/DashboardPage';
import PropertiesPage from '@/pages/PropertiesPage';
import PropertySetupPage from '@/pages/PropertySetupPage';
import BookingsPage from '@/pages/BookingsPage';
import CalendarPage from '@/pages/CalendarPage';
import ChannelsPage from '@/pages/ChannelsPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import ReportsPage from '@/pages/EnhancedReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import PlatformAdminPage from '@/pages/PlatformAdminPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminForgotPasswordPage from '@/pages/AdminForgotPasswordPage';
import MessagingPage from '@/pages/MessagingPage';
import GuestPortalPage from '@/pages/GuestPortalPage';
import GuestPaymentPortal from '@/pages/GuestPaymentPortal';
import UnifiedGuestPortal from '@/pages/UnifiedGuestPortal';
import WidgetEmbedPage from '@/pages/WidgetEmbedPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsConditionsPage from '@/pages/TermsConditionsPage';
import DocsPage from '@/pages/DocsPage';
import MarketplacePage from '@/pages/MarketplacePage';
import MarketplacePropertyPage from '@/pages/MarketplacePropertyPage';
import MarketplaceAdminPage from '@/pages/MarketplaceAdminPage';
import GuestLoginPage from '@/pages/GuestLoginPage';
import GuestGoogleCallbackPage from '@/pages/GuestGoogleCallbackPage';
import TeamPage from '@/pages/TeamPage';
import ReviewsPage from '@/pages/ReviewsPage';
import ReviewSubmitPage from '@/pages/ReviewSubmitPage';
import AIPricingPage from '@/pages/AIPricingPage';
import DemandForecastPage from '@/pages/DemandForecastPage';
import WidgetsPage from '@/pages/WordPressWidgetPage';
import MobileAppPage from '@/pages/MobileAppPage';
import WPWidgetEmbedPage from '@/pages/WPWidgetEmbedPage';
import PendingApprovalPage from '@/pages/PendingApprovalPage';
import AppLayout from '@/components/AppLayout';

// Standalone layout for Platform Admin (completely isolated)
const AdminLayout = () => {
  return <div className="min-h-screen bg-slate-900"><PlatformAdminPage /></div>;
};

import '@/App.css';

// Detect session_id from OAuth callback synchronously during render
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id BEFORE rendering any route
  // This prevents race conditions with ProtectedRoute
  // BUT: Skip this for marketplace/guest routes - they handle their own callbacks
  const isMarketplaceCallback = location.pathname.includes('/marketplace/auth');
  if (location.hash?.includes('session_id=') && !isMarketplaceCallback) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Admin routes FIRST - completely isolated */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin" element={<AdminLayout />} />
      <Route path="/admin/marketplace" element={<MarketplaceAdminPage />} />
      
      {/* Public Marketplace (iStayBnB) */}
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/property/:propertyId" element={<MarketplacePropertyPage />} />
      <Route path="/marketplace/login" element={<GuestLoginPage />} />
      <Route path="/marketplace/auth/google/callback" element={<GuestGoogleCallbackPage />} />
      
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/change-password" element={<ForcePasswordChangePage />} />
      <Route path="/pending-approval" element={<PendingApprovalPage />} />

      {/* Public legal pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsConditionsPage />} />
      
      {/* Public guest portal (no auth) */}
      <Route path="/guest/:portalToken" element={<GuestPortalPage />} />
      
      {/* Guest booking payment portal (no auth, secure token) */}
      <Route path="/guest/booking/:bookingToken" element={<GuestPaymentPortal />} />
      
      {/* Unified Guest Portal (email + booking ID login) */}
      <Route path="/guest-portal" element={<UnifiedGuestPortal />} />
      
      {/* Booking Widget Embed (public, no auth) */}
      <Route path="/widget/:widgetKey" element={<WidgetEmbedPage />} />
      <Route path="/widget/wp/:widgetKey" element={<WPWidgetEmbedPage />} />
      <Route path="/book/:widgetKey" element={<WidgetEmbedPage />} />
      
      {/* Public Review Submission (no auth, token-based) */}
      <Route path="/review/:token" element={<ReviewSubmitPage />} />

      {/* Protected tenant routes */}
      <Route path="/dashboard" element={<AppLayout><DashboardPage /></AppLayout>} />
      <Route path="/properties" element={<AppLayout><PropertiesPage /></AppLayout>} />
      <Route path="/properties/new/setup" element={<AppLayout><PropertySetupPage /></AppLayout>} />
      <Route path="/properties/:propertyId/setup" element={<AppLayout><PropertySetupPage /></AppLayout>} />
      <Route path="/bookings" element={<AppLayout><BookingsPage /></AppLayout>} />
      <Route path="/calendar" element={<AppLayout><CalendarPage /></AppLayout>} />
      <Route path="/ai-pricing" element={<AppLayout><AIPricingPage /></AppLayout>} />
      <Route path="/demand-forecast" element={<AppLayout><DemandForecastPage /></AppLayout>} />
      <Route path="/widgets" element={<Navigate to="/integrations" replace />} />
      <Route path="/channels" element={<Navigate to="/integrations" replace />} />
      <Route path="/integrations" element={<AppLayout><IntegrationsPage /></AppLayout>} />
      <Route path="/reports" element={<AppLayout><ReportsPage /></AppLayout>} />
      <Route path="/settings" element={<AppLayout><SettingsPage /></AppLayout>} />
      <Route path="/subscription" element={<AppLayout><SubscriptionPage /></AppLayout>} />
      <Route path="/payment-success" element={<AppLayout><SubscriptionPage /></AppLayout>} />
      <Route path="/messaging" element={<AppLayout><MessagingPage /></AppLayout>} />
      <Route path="/docs" element={<AppLayout><DocsPage /></AppLayout>} />
      <Route path="/team" element={<AppLayout><TeamPage /></AppLayout>} />
      <Route path="/reviews" element={<AppLayout><ReviewsPage /></AppLayout>} />
      <Route path="/mobile-app" element={<Navigate to="/dashboard" replace />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <DateFormatProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster />
        </BrowserRouter>
      </DateFormatProvider>
    </AuthProvider>
  );
}

export default App;
